import BackButton from "@/components/BackButton"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { Button } from "@/components/chadcn/Button"
import { useSelfQuery, useUpdatePatientProfileMutation } from "@/data/queries"
import { toPatientAppStateApiDto } from "@/model/PatientAppState"
import { ROUTES } from "@/routes"
import { Navigate, useNavigate, useSearchParams } from "react-router"

const IGLUCOSE_SETUP_YOUTUBE_URL = "https://youtube.com/embed/ERUO1TRr6Bk?autoplay=0"
const IGLUCOSE_PLUS_SETUP_YOUTUBE_URL = "https://www.youtube.com/embed/3NUHW1zTsh8?autoplay=0"
const ONBOARDING_INTRODUCTION_VIMEO_URL = "https://player.vimeo.com/video/949182782"

const SETUP_STEPS = ["welcome", "videos"] as const
type SetupStep = (typeof SETUP_STEPS)[number]

const isValidStep = (value: string | null): value is SetupStep =>
  SETUP_STEPS.includes(value as SetupStep)

// --- Individual step components ---

const BloodGlucoseInfoStep = ({ onBack, onNext }: { onBack: () => void; onNext: () => void }) => (
  <div className="flex min-h-[calc(100vh-32px)] flex-col gap-6">
    <BackButton customClickHandler={onBack} />
    <div className="flex flex-1 flex-col gap-4">
      <h1 className="text-h1 text-primary-contrast">
        Understanding blood glucose and its vital role in your body
      </h1>
      <p className="text-base text-primary-contrast">
        Glucose is a type of sugar found in the bloodstream. Blood glucose is commonly referred to
        as blood sugar. Glucose is the primary source of energy for all of the cells in your body,
        especially your brain and muscles
      </p>
      <p className="text-base text-primary-contrast">
        When you take a blood glucose measurement, the number you see reflects how much glucose is
        in your bloodstream at that moment
      </p>
      <div className="aspect-video w-full overflow-hidden rounded-lg border border-secondary bg-black">
        <iframe
          allowFullScreen
          src={ONBOARDING_INTRODUCTION_VIMEO_URL}
          title="Welcome to Bloomlife Connects"
          className="h-full w-full border-0"
        />
      </div>
    </div>
    <Button type="button" className="w-full py-2 text-base" onClick={onNext}>
      Continue
    </Button>
  </div>
)

const SetupVideosStep = ({ onDone, isDone }: { onDone: () => void; isDone: boolean }) => (
  <div className="flex min-h-[calc(100vh-32px)] flex-col gap-6">
    <div className="flex flex-1 flex-col gap-4">
      <h1 className="text-h1 text-primary-contrast">
        Setting up your connected blood glucose monitor
      </h1>
      <p className="text-base text-primary-contrast">
        Please follow the instructions below depending on the device you have
      </p>
      <section className="flex flex-col gap-2">
        <h2 className="text-center text-base font-semibold text-primary-contrast">iGlucose</h2>
        <div className="aspect-video w-full overflow-hidden rounded-lg border border-secondary bg-black">
          <iframe
            allowFullScreen
            src={IGLUCOSE_SETUP_YOUTUBE_URL}
            title="iGlucose setup instructions"
            className="h-full w-full border-0"
          />
        </div>
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="text-center text-base font-semibold text-primary-contrast">iGlucose Plus</h2>
        <div className="aspect-video w-full overflow-hidden rounded-lg border border-secondary bg-black">
          <iframe
            allowFullScreen
            src={IGLUCOSE_PLUS_SETUP_YOUTUBE_URL}
            title="iGlucose Plus setup instructions"
            className="h-full w-full border-0"
          />
        </div>
      </section>
    </div>
    <Button
      type="button"
      className="flex w-full gap-3 py-2 text-base"
      disabled={isDone}
      onClick={onDone}
    >
      {isDone && <Spinner />}
      Continue
    </Button>
  </div>
)

// --- Main page ---

const BloodGlucoseDeviceSetupPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const stepParam = searchParams.get("step")

  const { data: self } = useSelfQuery()
  const { mutate: updatePatientProfile, isPending: savingProfile } =
    useUpdatePatientProfileMutation()

  if (!isValidStep(stepParam)) {
    return <Navigate to="/blood-glucose-device-setup?step=welcome" replace />
  }

  const step = stepParam

  if (!self) {
    return (
      <Page showAppbar={false}>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </Page>
    )
  }

  const goToStep = (next: SetupStep) => setSearchParams({ step: next })

  // Backing out of device setup un-finishes the profile so the patient
  // re-enters onboarding on their next visit (same contract as the old app).
  const handleWelcomeBack = () => {
    if (self.patientAppState.profileFormFilled) {
      updatePatientProfile(
        {
          patientId: self.id,
          profile: {
            patientAppState: toPatientAppStateApiDto({
              profileFormFilled: false,
              bloodGlucoseDeviceSetupCompleted:
                self.patientAppState.bloodGlucoseDeviceSetupCompleted,
            }),
          },
        },
        {
          onSettled: () => navigate(-1),
        },
      )
    } else {
      navigate(-1)
    }
  }

  const handleDone = () => {
    updatePatientProfile(
      {
        patientId: self.id,
        profile: {
          patientAppState: toPatientAppStateApiDto({
            profileFormFilled: self.patientAppState.profileFormFilled,
            bloodGlucoseDeviceSetupCompleted: true,
          }),
        },
      },
      {
        onSuccess: () =>
          navigate(ROUTES.DASHBOARD, { replace: true, state: { initialLogin: true } }),
        onError: () => navigate(ROUTES.DASHBOARD, { replace: true, state: { initialLogin: true } }),
      },
    )
  }

  return (
    <Page showAppbar={false}>
      {step === "welcome" && (
        <BloodGlucoseInfoStep onBack={handleWelcomeBack} onNext={() => goToStep("videos")} />
      )}
      {step === "videos" && <SetupVideosStep onDone={handleDone} isDone={savingProfile} />}
    </Page>
  )
}

export default BloodGlucoseDeviceSetupPage
